import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Clock3, UserCheck, CalendarDays, FileSearch } from "lucide-react";
import Seo, { breadcrumbSchema, articleSchema, faqSchema } from "../components/Seo";
import { Reveal, FaqList, CtaBand, Crumbs } from "../components/Shared";
import { NewsletterForm } from "../components/NewsletterForm";
import { ARTICLES, getArticle } from "../data/articles";

const BODY_IMAGES = [
  { src: "/images/blog/class-color.jpg", alt: "A full class moving through synchronized barbell work at Boston Iron Grit", pos: "center 40%" },
  { src: "/images/blog/pullup-athlete.jpg", alt: "Member driving through a pull-up on the rig", pos: "center 35%" },
  { src: "/images/blog/pt-bench-coaching.jpg", alt: "Coach guiding a member through a bench press", pos: "center 18%" },
  { src: "/images/blog/push-press.jpg", alt: "Member locking out a barbell push press in class", pos: "center 35%" },
  { src: "/images/blog/member-squat.jpg", alt: "Member setting up under a back squat", pos: "center 22%" },
  { src: "/images/blog/kb-carry.jpg", alt: "Focused member mid-carry during a conditioning workout", pos: "center 30%" },
  { src: "/images/blog/db-press.jpg", alt: "Member pressing dumbbells overhead", pos: "center 30%" },
  { src: "/images/blog/toes-to-bar.jpg", alt: "Athlete performing toes-to-bar on the pull-up bar", pos: "center 35%" },
  { src: "/images/blog/beginner-wallball.jpg", alt: "Beginner working through wall-ball squats with coaching nearby", pos: "center 18%" },
  { src: "/images/gym/rower.webp", alt: "Steady-state rowing on the Concept2 erg", pos: "center 30%" },
  { src: "/images/gym/masters-clean.jpg", alt: "Masters athlete catching a power clean", pos: "center 30%" },
  { src: "/images/gym/miguel-jerk.jpg", alt: "Coach Miguel finishing a jerk in competition", pos: "center 25%" },
  { src: "/images/gym/kb-swing.jpg", alt: "Kettlebell swing at the top of the arc", pos: "center 25%" },
  { src: "/images/gym/happy-member.jpg", alt: "Smiling member carrying dumbbells between stations", pos: "center 25%" },
  { src: "/images/gym/plate-load.jpg", alt: "Loading bumper plates onto a barbell before a lift", pos: "center 40%" },
  { src: "/images/gym/squat-bw.jpg", alt: "Member bracing under a heavy squat", pos: "center 25%" },
  { src: "/images/gym/class-wide.jpg", alt: "The whole class working together on the gym floor", pos: "center 40%" },
  { src: "/images/gym/wallball-coaching.jpg", alt: "Coach cueing a member through wall-ball reps", pos: "center 30%" },
];

function pickBodyImages(a) {
  if (a.bodyImages) return a.bodyImages;
  const hash = a.slug.split("").reduce((n, ch) => n + ch.charCodeAt(0), 0);
  const pool = BODY_IMAGES.filter((img) => img.src !== a.image);
  const first = pool[hash % pool.length];
  const second = pool[(hash + 7) % pool.length];
  return second.src === first.src ? [first] : [first, second];
}

const InlinePhoto = ({ img }) => (
  <figure className="mt-12 border border-white/10 overflow-hidden" data-testid="article-inline-photo">
    <img
      src={img.src}
      alt={img.alt}
      style={{ objectPosition: img.pos || "center 30%" }}
      className="w-full h-64 sm:h-80 object-cover"
      loading="lazy"
      decoding="async"
    />
    <figcaption className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500 bg-[#0D0D0F]">
      {img.alt} — Boston Iron Grit, West Roxbury
    </figcaption>
  </figure>
);

export default function BlogPost() {
  const { slug } = useParams();
  const a = getArticle(slug);
  if (!a) return <ArticleNotFound />;
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Blog", path: "/blog" },
    { name: a.title, path: `/blog/${a.slug}` },
  ];
  const related = (a.related || []).map(getArticle).filter(Boolean);
  const newsletterAt = Math.max(0, Math.ceil(a.sections.length * 0.4) - 1);
  const bodyImages = pickBodyImages(a);
  const photoAt = { 0: bodyImages[0] };
  if (bodyImages[1] && a.sections.length > 2) photoAt[a.sections.length - 2] = bodyImages[1];

  return (
    <>
      <Seo
        title={`${a.title} | Boston Iron Grit`}
        description={a.metaDescription}
        path={`/blog/${a.slug}`}
        ogType="article"
        ogImage={a.image}
        schemas={[breadcrumbSchema(crumbs), articleSchema(a), faqSchema(a.faqs)]}
      />

      {/* Article hero */}
      <section className="relative bg-[#0A0A0B] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${a.image})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-[#0A0A0B]/80" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-6 pt-14 pb-16 lg:pt-20 lg:pb-24">
          <Reveal>
            <Crumbs items={[crumbs[0], crumbs[1], { name: "Article", path: crumbs[2].path }]} />
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.28em] text-[#E56A79]">// {a.category}</p>
            <h1 className="mt-4 font-heading font-extrabold uppercase text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.02] tracking-tight">
              {a.title}
            </h1>
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[11px] uppercase tracking-[0.14em] text-neutral-400">
              <span className="flex items-center gap-1.5"><UserCheck size={12} className="text-[#9D0423]" /> {a.author}</span>
              <span className="text-neutral-600">/</span>
              <span>Reviewed by {a.reviewer}</span>
              <span className="text-neutral-600">/</span>
              <span className="flex items-center gap-1.5"><CalendarDays size={12} /> Updated {a.updated}</span>
              <span className="text-neutral-600">/</span>
              <span className="flex items-center gap-1.5"><Clock3 size={12} /> {a.readTime}</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Body */}
      <article className="bg-[#0A0A0B] py-14 lg:py-20">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <p className="text-lg lg:text-xl text-neutral-200 leading-relaxed border-l-2 border-[#9D0423] pl-5">{a.excerpt}</p>
          </Reveal>

          {/* Comparison table if present */}
          {a.comparison && (
            <Reveal>
              <div className="mt-12 overflow-x-auto border border-white/10">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-[#141416]">
                      {a.comparison.headers.map((h) => (
                        <th key={h} className="px-5 py-4 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-300 border-b border-white/10">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {a.comparison.rows.map((row) => (
                      <tr key={row[0]} className="border-b border-white/5 last:border-0">
                        {row.map((cell, j) => (
                          <td key={`${row[0]}-${j}`} className={`px-5 py-4 text-sm ${j === 0 ? "text-white font-semibold" : "text-neutral-400"}`}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Reveal>
          )}

          {a.sections.map((s, i) => (
            <React.Fragment key={s.h2}>
              <Reveal>
                <section className="mt-12">
                  <h2 className="font-heading font-extrabold uppercase text-2xl lg:text-3xl text-white tracking-tight">{s.h2}</h2>
                  {s.paras.map((p) => (
                    <p key={p.slice(0, 48)} className="mt-5 text-[16px] text-neutral-300 leading-[1.85]">{p}</p>
                  ))}
                  {s.list && (
                    <ul className="mt-5 space-y-2.5">
                      {s.list.map((li) => (
                        <li key={li} className="flex items-start gap-3 text-[15px] text-neutral-300 leading-relaxed">
                          <span className="mt-2.5 w-1.5 h-1.5 bg-[#9D0423] shrink-0" />
                          {li}
                        </li>
                      ))}
                    </ul>
                  )}
                  {s.link && (
                    <Link to={s.link.to} className="mt-5 inline-flex items-center gap-2 text-[15px] text-[#E56A79] hover:text-white font-medium transition-colors">
                      {s.link.text} <ArrowRight size={15} />
                    </Link>
                  )}
                </section>
              </Reveal>
              {photoAt[i] && (
                <Reveal>
                  <InlinePhoto img={photoAt[i]} />
                </Reveal>
              )}
              {i === newsletterAt && (
                <Reveal>
                  <aside className="mt-12 p-7 bg-[#121214] border border-white/10" data-testid="blog-newsletter-inline">
                    <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#E56A79] mb-2">The Grit Report</p>
                    <p className="text-[15px] text-neutral-300 leading-relaxed mb-4">
                      Liked this? The Grit Report goes out monthly — training tips, member stories and challenge dates from Boston Iron Grit.
                    </p>
                    <div className="max-w-md">
                      <NewsletterForm source="blog-inline" idPrefix="blog-newsletter-inline" />
                    </div>
                  </aside>
                </Reveal>
              )}
            </React.Fragment>
          ))}

          {/* Local next step */}
          <Reveal>
            <div className="mt-14 p-7 bg-[#121214] border border-[#9D0423]/30">
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#E56A79] mb-3">Local Next Step</p>
              <p className="text-[15px] text-neutral-300 leading-relaxed">
                Ready to put this into practice in West Roxbury?{" "}
                <Link to={a.service.to} className="text-white underline decoration-[#9D0423] underline-offset-4 hover:text-[#E56A79] transition-colors">{a.service.label}</Link>{" "}
                or{" "}
                <Link to="/get-started" className="text-white underline decoration-[#9D0423] underline-offset-4 hover:text-[#E56A79] transition-colors">book a free goal review</Link>{" "}
                at Boston Iron Grit.
              </p>
            </div>
          </Reveal>

          {/* FAQ */}
          <Reveal>
            <div className="mt-14">
              <FaqList faqs={a.faqs} title="FAQ" />
            </div>
          </Reveal>
        </div>
      </article>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="bg-[#0D0D0F] border-y border-white/5 py-16">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="font-heading font-extrabold uppercase text-2xl text-white tracking-tight mb-8">Keep reading</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="group flex gap-5 p-5 bg-[#121214] border border-white/5 hover:border-[#9D0423]/40 transition-colors">
                  <img src={r.image} alt={r.title} style={{ objectPosition: r.imagePos || "center 30%" }} className="w-28 h-24 object-cover shrink-0 opacity-75 group-hover:opacity-100 transition-opacity" loading="lazy" decoding="async" />
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#E56A79]">{r.category}</p>
                    <h3 className="mt-1.5 font-heading font-bold uppercase text-base text-white tracking-tight leading-snug group-hover:text-[#E56A79] transition-colors">{r.title}</h3>
                    <span className="mt-2 inline-block font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">{r.readTime}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaBand />
    </>
  );
}

function ArticleNotFound() {
  const suggestions = ARTICLES.slice(0, 3);
  return (
    <>
      <Seo
        title="Article Not Found | Boston Iron Grit"
        description="That article doesn't exist. Browse training, nutrition and recovery articles from Boston Iron Grit in West Roxbury."
        noindex
      />
      <section className="min-h-[70vh] bg-[#0A0A0B] flex items-center" data-testid="article-not-found">
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <Reveal>
            <FileSearch size={40} className="text-[#9D0423] mx-auto" />
            <p className="mt-6 font-heading font-extrabold text-7xl text-white/10">404</p>
            <h1 className="mt-2 font-heading font-extrabold uppercase text-4xl sm:text-5xl text-white tracking-tight">
              Article not <span className="text-[#9D0423]">found.</span>
            </h1>
            <p className="mt-5 text-neutral-400 text-base leading-relaxed max-w-md mx-auto">
              That post doesn't exist or may have moved. Try one of these instead:
            </p>
            <div className="mt-9 grid sm:grid-cols-3 gap-4 text-left">
              {suggestions.map((s) => (
                <Link
                  key={s.slug}
                  to={`/blog/${s.slug}`}
                  data-testid={`article-notfound-suggestion-${s.slug}`}
                  className="group bg-[#121214] border border-white/8 hover:border-[#9D0423]/60 p-5 transition-colors"
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{s.category}</p>
                  <p className="mt-2 font-heading font-bold uppercase text-sm text-white tracking-tight leading-snug group-hover:text-[#E56A79] transition-colors">{s.title}</p>
                </Link>
              ))}
            </div>
            <Link
              to="/blog"
              data-testid="article-notfound-blog-link"
              className="mt-9 inline-flex items-center gap-2 bg-[#9D0423] hover:bg-[#7E031C] text-white font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-7 py-4 transition-colors"
            >
              Browse All Articles <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
